import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Instagram, Facebook, Star } from "lucide-react";
import { LOGO } from "../mock";
import { track } from "../lib/analytics";

const MEDICAL_DISCLAIMER =
  "Sassy Full Stack Fitness provides coaching, fitness, and nutrition services. We do not practice medicine, diagnose, or prescribe. Medical services, laboratory testing, and prescriptions are provided by independent licensed providers and pharmacies. Nothing on this site is medical advice. Consult your physician before beginning any fitness, nutrition, or supplementation program.";

const Footer = ({ extraDisclaimer = null, legalOnly = false }) => {
  const year = new Date().getFullYear();
  const navigate = useNavigate();
  const location = useLocation();
  const goToSection = (id) => (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (location.pathname === "/") {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/", { state: { scrollTo: id } });
    }
  };
  return (
    <footer className="border-t border-white/10 bg-black/40">
      <div className="w-[90%] max-w-[1200px] mx-auto py-14">
        <div className="flex flex-col items-center text-center gap-8 md:flex-row md:items-center md:justify-between md:text-left">
          <div className="flex items-center">
            <img src={LOGO} alt="Sassy Full Stack Fitness" className="h-12 w-auto object-contain" />
          </div>

          <nav className="flex flex-wrap justify-center md:justify-start gap-x-5 gap-y-2 md:gap-x-6 text-sm font-semibold text-white/60">
            {!legalOnly && (
              <>
                <a href="/#matrix" onClick={goToSection("matrix")} className="whitespace-nowrap hover:text-white transition-colors" data-testid="footer-matrix">The Matrix</a>
                <a href="/#how" onClick={goToSection("how")} className="whitespace-nowrap hover:text-white transition-colors" data-testid="footer-how">How It Works</a>
                <a href="/#about" onClick={goToSection("about")} className="whitespace-nowrap hover:text-white transition-colors" data-testid="footer-about">About</a>
                <a href="/#success" onClick={goToSection("success")} className="whitespace-nowrap hover:text-white transition-colors" data-testid="footer-success">Success Stories</a>
                <a href="/#faq" onClick={goToSection("faq")} className="whitespace-nowrap hover:text-white transition-colors" data-testid="footer-faq">FAQ</a>
              </>
            )}
            <a href="/privacy" className="whitespace-nowrap hover:text-white transition-colors" data-testid="footer-privacy">Privacy</a>
            <a href="/terms" className="whitespace-nowrap hover:text-white transition-colors" data-testid="footer-terms">Terms</a>
          </nav>

          <div className="flex items-center gap-4">
            <div className="flex gap-3">
              <a
                href="https://www.instagram.com/sassy_sandraguy"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track("outbound_click", { url: "instagram", label: "Instagram" })}
                className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-white/60 hover:text-primary hover:border-primary/40 transition-colors"
                aria-label="Instagram @sassy_sandraguy"
                data-testid="footer-instagram"
              >
                <Instagram size={18} />
              </a>
              <a
                href="https://www.facebook.com/share/1LNAba6j33/?mibextid=wwXIfr"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track("outbound_click", { url: "facebook", label: "Facebook" })}
                className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-white/60 hover:text-primary hover:border-primary/40 transition-colors"
                aria-label="Facebook"
                data-testid="footer-facebook"
              >
                <Facebook size={18} />
              </a>
            </div>
            <a
              href="https://www.trustpilot.com/review/sassyfitness.com"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("outbound_click", { url: "trustpilot", label: "Trustpilot" })}
              className="inline-flex items-center gap-1.5 text-white/60 hover:text-primary transition-colors"
              data-testid="footer-trust"
              aria-label="Review us on Trustpilot"
            >
              <Star size={17} strokeWidth={0} className="fill-current" />
              <span className="text-[13px] tracking-tight whitespace-nowrap">Review us on Trustpilot</span>
            </a>
          </div>
        </div>

        <div className="mt-10 pt-8 border-t border-white/10 space-y-4">
          <p className="text-sm text-white/55 leading-relaxed">{MEDICAL_DISCLAIMER}</p>
          {extraDisclaimer && (
            <p className="text-sm text-white/55 leading-relaxed">{extraDisclaimer}</p>
          )}
          <p className="text-xs text-white/40">© {year} Sassy Full Stack Fitness. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
